import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Save, FileText, Loader2, ChevronDown } from "lucide-react";
import { API_URL, getAuthHeader } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface EmailComposerProps {
    subject: string;
    setSubject: (subject: string) => void;
    body: string;
    setBody: (body: string) => void;
}

const EmailComposer: React.FC<EmailComposerProps> = ({
    subject,
    setSubject,
    body,
    setBody
}) => {
    const { token } = useAuth();
    const [templates, setTemplates] = React.useState<any[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = React.useState(false);
    const [isSavingTemplate, setIsSavingTemplate] = React.useState(false);
    const [showSaveDialog, setShowSaveDialog] = React.useState(false);
    const [templateName, setTemplateName] = React.useState("");

    // Refs for cursor tracking
    const subjectInputRef = React.useRef<HTMLInputElement>(null);
    const quillRef = React.useRef<ReactQuill>(null);
    const [lastFocused, setLastFocused] = React.useState<'subject' | 'body' | null>(null);
    const [selectionIndex, setSelectionIndex] = React.useState<number | null>(null);

    const variables = ["{{prenom}}", "{{nom}}", "{{email}}", "{{nom_classe}}", "{{url_classe}}", "{{url_acces}}", "{{heure_debut}}"];

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'clean']
        ],
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link'
    ];

    const fetchTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
            const response = await fetch(`${API_URL}/email-templates`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                setTemplates(data);
            }
        } catch (error) {
            console.error("Failed to fetch templates", error);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    React.useEffect(() => {
        fetchTemplates();
    }, [token]);

    const handleSaveTemplate = async () => {
        if (!templateName.trim()) {
            toast.error("Veuillez entrer un nom pour le template");
            return;
        }
        setIsSavingTemplate(true);
        try {
            const response = await fetch(`${API_URL}/email-templates`, {
                method: 'POST',
                headers: getAuthHeader(token),
                body: JSON.stringify({
                    name: templateName,
                    subject,
                    body,
                }),
            });
            if (response.ok) {
                toast.success("Template sauvegardé");
                setShowSaveDialog(false);
                setTemplateName("");
                fetchTemplates();
            } else {
                toast.error("Erreur lors de la sauvegarde");
            }
        } catch (error) {
            console.error("Failed to save template", error);
        } finally {
            setIsSavingTemplate(false);
        }
    };

    const loadTemplate = (template: any) => {
        setSubject(template.subject);
        setBody(template.body);
    };

    const addVariable = (variable: string) => {
        if (lastFocused === 'subject' && subjectInputRef.current) {
            const input = subjectInputRef.current;
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;

            const newSubject = subject.substring(0, start) + variable + subject.substring(end);
            setSubject(newSubject);

            // Restore focus and cursor position after render
            setTimeout(() => {
                input.focus();
                input.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        } else if (lastFocused === 'body' && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true); // true = focus if needed, but we check lastFocused

            if (range) {
                editor.insertText(range.index, variable);
                // Move cursor after variable
                setTimeout(() => {
                    editor.setSelection(range.index + variable.length, 0);
                }, 0);
            } else {
                // Fallback: append
                setBody(body + variable);
            }
        } else {
            // Default fallback if no focus detected: append to body
            setBody(body + variable);
        }
    };

    return (
        <div className="space-y-4 border rounded-xl p-5 bg-white shadow-sm">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-2">
                <h3 className="text-sm font-bold text-slate-900">Email de Notification</h3>
                <div className="flex gap-2">
                    <div className="relative group">
                        <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold border-slate-200">
                            {isLoadingTemplates ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <FileText className="h-3 w-3 mr-2" />}
                            Charger un template
                            <ChevronDown className="h-3 w-3 ml-2" />
                        </Button>
                        <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
                            {templates.length === 0 ? (
                                <div className="px-3 py-2 text-[10px] text-slate-400 italic">Aucun template sauvegardé</div>
                            ) : (
                                templates.map((t) => (
                                    <button
                                        key={t.id}
                                        className="w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 transition-colors"
                                        onClick={() => loadTemplate(t)}
                                    >
                                        {t.name}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-[11px] font-bold text-slate-500 hover:text-primary transition-colors"
                        onClick={() => setShowSaveDialog(true)}
                    >
                        <Save className="h-3 w-3 mr-2" />
                        Sauvegarder
                    </Button>
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="email-subject" className="text-xs font-semibold uppercase text-slate-500">Objet</Label>
                <Input
                    id="email-subject"
                    ref={subjectInputRef}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    onFocus={() => setLastFocused('subject')}
                    placeholder="Invitation: {{nom_classe}}"
                    className="bg-white"
                />
            </div>

            <div className="space-y-2">
                <div className="flex justify-between items-center mb-1">
                    <Label className="text-xs font-semibold uppercase text-slate-500">Message</Label>
                    <div className="flex flex-wrap gap-1">
                        {variables.map((v) => (
                            <Badge
                                key={v}
                                variant="outline"
                                className="cursor-pointer hover:bg-primary hover:text-white transition-colors text-xs"
                                onClick={() => addVariable(v)}
                            >
                                {v}
                            </Badge>
                        ))}
                    </div>
                </div>
                <div className="rich-text-editor">
                    <style>{`
                        .rich-text-editor .ql-container {
                            border-bottom-left-radius: 0.75rem;
                            border-bottom-right-radius: 0.75rem;
                            font-family: inherit;
                            min-height: 200px;
                        }
                        .rich-text-editor .ql-toolbar {
                            border-top-left-radius: 0.75rem;
                            border-top-right-radius: 0.75rem;
                            background: #f8fafc;
                        }
                        .rich-text-editor .ql-editor {
                            font-size: 0.875rem;
                            line-height: 1.5;
                        }
                    `}</style>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={body}
                        onChange={setBody}
                        onFocus={() => setLastFocused('body')}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Bonjour {{prenom}}, écrivez ici le contenu de votre email..."
                    />
                </div>
            </div>

            <p className="text-[10px] text-slate-400 italic">
                * Variable values will be replaced automatically for each student.
            </p>
            <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Sauvegarder comme template</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-3">
                        <Label>Nom du template</Label>
                        <Input
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="ex: Invitation par défaut"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Annuler</Button>
                        <Button onClick={handleSaveTemplate} disabled={isSavingTemplate}>
                            {isSavingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Sauvegarder
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EmailComposer;
